<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { definePage } from "unplugin-vue-router/runtime";
import Loading from "@/components/shared/Loading.vue";
import MyPracticesTable from "@/components/models/company/table/MyPracticesTable.vue";
import OrganizationSectionHero from "@/components/shared/headers/OrganizationSectionHero.vue";
import SectionCard from "@/components/ui/cards/SectionCard.vue";
import PageSearchFilter from "@/components/shared/table/PageSearchFilter.vue";
import { useCanAccessOfficesManagement } from "@/composables/shared/useSectionPermissions";
import { useOrganizationLevel, useOrganizationMode } from "@/composables/shared/useMyOrganization";
import { ORGANIZATION_ACCENTS } from "@/components/shared/navbar/useNavbarSubItems";
import { formatCount } from "@/utils/Formatters";
import welcomeImg from "@/assets/welcome.png";

definePage({
  meta: {
    title: "generic.fields.sections.officesPracticesHeading",
    checkPrivileges: () => useCanAccessOfficesManagement().value,
  },
});

const { t } = useI18n();
const organizationMode = useOrganizationMode();
const { companies, companiesQuery, msos, isList, isEmpty } = useOrganizationLevel("practices");

const ownerMso = computed(() => (msos.value.length === 1 ? msos.value[0] : undefined));
const ownerName = computed(() => ownerMso.value?.name);

// Offices are aggregated across every practice, so that is the count carrying "in total" — the
// practice count is just the length of the list on screen.
const officeCount = computed(() =>
  companies.value.reduce((sum, company) => sum + (company.practice_count ?? 0), 0)
);

const practicesSearch = ref<string | null>(null);

const heroStats = computed(() => [
  {
    label: t("generic.fields.sections.officesPracticesHeading"),
    value: formatCount(companies.value.length) ?? "0",
  },
  {
    label: t("generic.fields.sections.offices"),
    value: t("generic.entity.inTotal", { entity: formatCount(officeCount.value) ?? "0" }),
  },
]);
</script>

<template>
  <div
    data-tour="tour-offices-page"
    class="tw:flex tw:flex-col tw:gap-5 tw:pb-4! tw:sm:pb-6!"
  >
    <organization-section-hero
      v-if="isList"
      :eyebrow="t('generic.fields.sections.officesPracticesHeading')"
      :title="ownerName ?? t('generic.fields.sections.myPractices')"
      :mode="organizationMode"
      :mso="ownerMso"
      :accent-color="ORGANIZATION_ACCENTS.practices"
      icon="mdi-office-building-outline"
      :stats="heroStats"
    />

    <div
      v-if="isEmpty"
      class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:rounded-xl tw:bg-surface tw:py-16! tw:text-center"
    >
      <v-img
        :src="welcomeImg"
        :alt="t('generic.project.name')"
        height="80"
        width="auto"
        inline
      />
      <h1 class="tw:text-2xl tw:text-on-surface/90 md:tw:text-4xl tw:font-bold tw:mt-8! tw:mb-2!">
        {{ t("generic.messages.noEntity", { entity: t("generic.entity.company", 2) }) }}
      </h1>
      <p
        class="tw:text-ms tw:font-medium tw:text-on-surface/85 tw:max-w-85 tw:mx-auto! tw:leading-5"
      >
        {{ t("models.practice.officesManagement.emptyDescription") }}
      </p>
    </div>

    <Loading
      v-else-if="!isList"
      :size="32"
      class="tw:my-6!"
    />

    <section-card
      v-else
      icon="mdi-office-building-outline"
    >
      <template #title>
        {{ t("generic.fields.sections.officesPracticesHeading") }}
        <span class="tw:text-[13px] tw:font-medium tw:text-on-surface/45">
          {{ `(${companies.length})` }}
        </span>
      </template>

      <template
        v-if="ownerName"
        #subtitle
      >
        <i18n-t
          keypath="generic.fields.sections.practicesWithinDso"
          scope="global"
          tag="span"
        >
          <template #name>
            <strong>{{ ownerName }}</strong>
          </template>
        </i18n-t>
      </template>

      <template #actions>
        <div class="tw:w-full tw:md:w-[280px]">
          <page-search-filter
            v-model="practicesSearch"
            :placeholder="t('generic.tables.searchByName')"
          />
        </div>
      </template>

      <my-practices-table
        v-model:search="practicesSearch"
        :query="companiesQuery"
      />
    </section-card>
  </div>
</template>
